<template>
  <div class="shared-page">
    <lottie
      v-if="$wait.waiting('fetching quiz page')"
      :options="defaultOptions"
      :height="400"
      :width="400"
    />
    <template v-else>
      <template v-if="page">
        <QuizChapterName :title="chapterName" />
        <QuizHeader :pageType="page.page_type" />
        <ContentViewer
          class="min-h-120"
          :page="page"
          :content="page.content"
          :isOXQuiz="isOXQuiz(options)"
        />
        <template v-if="page.answer_view_type === 'control'
          && isMultipleChoice(page)"
        >
          <QuizOptions
            :options="options"
          />
          <QuizOptionsControl
            :options="options"
            :onOptionClick="handleQuizOptionClick"
            :isSolvedQuiz="isSolvedQuiz"
            :answerId="answerId"
            :correctAnswerId="correctAnswerId"
          />
        </template>
        <QuizOptionsListControl
          v-if="page.answer_view_type === 'list'
          && !isOXQuiz(options)
          && isMultipleChoice(page)"
          :options="options"
          :onOptionClick="handleQuizOptionClick"
          :isSolvedQuiz="isSolvedQuiz"
          :answerId="answerId"
          :correctAnswerId="correctAnswerId"
        />
        <QuizOXControl
          v-if="page.answer_view_type === 'list'
          && page.page_type.name === 'multiple_choice'
          && isOXQuiz(options)"
          :onOptionClick="handleQuizOptionClick"
          :isSolvedQuiz="isSolvedQuiz"
          :quiz="quiz"
          :options="options"
        />
        <QuizTextEntryControl
          v-if="page.page_type.name === 'text_entry' && !isSolvedQuiz"
          :onSetSelectedOption="setSharedQuizOption"
        />
        <QuizResult
          v-if="isSolvedQuiz"
          ref="quizResult"
          :quiz="page"
          :quizResult="sharedPageResults"
          :isOXQuiz="isOXQuiz(options)"
        />
        <QuizDescription
          v-if="isSolvedQuiz"
          :quiz="page"
        />
        <QuizResultStatistics
          v-if="isSolvedQuiz"
          :quizResult="sharedPageResults"
          :quizResultCache="page.quiz_result_cache"
        />
        <QuizAnswerStatistics
          v-if="isSolvedQuiz && page.page_type.name === 'multiple_choice'"
          :quiz="page"
          :answerStatistics="page.answer_statistics"
        />
        <div class="container-sm pt-20 pb-20">
          <button
            v-if="!isSolvedQuiz"
            class="btn-lg btn-block btn-purple-outlined mb-20"
            @click="handleQuizSubmitClick"
            :disabled="!isSelectedOption || isSolvedQuiz"
          >
            <LoadingBounce
              v-if="$wait.waiting('create quiz result')"
              colorType="secondary"
            />
            <template v-else>
              정답확인
            </template>
          </button>
          <button
            class="btn-lg btn-block btn-purple"
            @click="handleDeppLinkClick"
          >
            클래스팅으로 이동하기
          </button>
        </div>
        <QuizResultModal
          v-if="isQuizResultModal"
          :isCorrect="sharedPageResults.is_correct"
          @onClick="handleQuizResultModalClose"
        />
      </template>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import { isEmpty, now, ceil, filter, find } from 'lodash';
import lottie from 'vue-lottie';

import quiz from '@/mixins/quiz';
import labels from '@/mixins/labels';

import QuizResultModal from '../components/QuizResultModal';
import { setDeepLink, getLocalStorageItems, setLocalStorageItems } from '../../../utils';
import { bouncingBallData } from '../../../assets/lottie';
import { sharedPageResultsKey } from '../../../constants';

export default {
  name: 'shared-page',

  data() {
    return {
      defaultOptions: {
        animationData: bouncingBallData,
      },
      isQuizResultModal: false,
      now: null,
      duration: null,
    };
  },

  components: {
    lottie,
    QuizResultModal,
  },

  mixins: [
    quiz,
    labels,
  ],

  computed: {
    ...mapGetters({
      routeQuery: 'getRouteQuery',
      page: 'getSharedPage',
      isSolvedQuiz: 'isSolvedSharedPage',
      selectedOption: 'getSelectedSharedQuizOption',
      sharedPageResults: 'getSharedPageResults',
      answerId: 'getAnswerId',
      correctAnswerId: 'getCorrectAnswerId',
    }),

    options() {
      return this.getQuizOptions(this.page.answers);
    },

    isSelectedOption() {
      return !isEmpty(this.selectedOption);
    },

    subjectName() {
      return this.getTranslatedName(this.getSubject(this.page.subject_id));
    },

    /**
       * @todo SharedPage에서 과목정보 데이터 추가할건지 정하기
       */
    chapterName() {
      return this.page.chapter_name;
    },
  },

  methods: {
    ...mapActions([
      'fetchSharedPage',
      'createSharedPageResults',
      'setSharedQuizOption',
      'fetchSharedPageResult',
    ]),

    handleQuizOptionClick(option) {
      this.setSharedQuizOption(option);
    },

    handleQuizSubmitClick() {
      this.$wait.start('create quiz result');

      this.duration = ceil((now() - this.now) / 1000);

      const data = {
        page_id: this.page.id,
        answer_id: this.selectedOption.id || '',
        shared_page_id: this.page.shared_page.id,
        duration: this.duration,
        value: this.selectedOption.value || '',
      };

      this.createSharedPageResults(data)
        .then(() => Vue.nextTick().then(() => this.$refs.quizResult.$el.scrollIntoView(true)))
        .then(() => this.handleQuizResultModalOpen())
        .then(() => {
          const quizResults = [
            ...getLocalStorageItems(sharedPageResultsKey),
            {
              code: this.routeQuery.code,
              shared_page_results_id: this.sharedPageResults.id,
              expired_at: this.$moment().add(1, 'day').toDate(),
            },
          ];

          setLocalStorageItems(sharedPageResultsKey, quizResults);
        })
        .then(() => this.$wait.end('create quiz result'))
        .then(() => setTimeout(() => { this.handleQuizResultModalClose(); }, 2000));
    },

    handleDeppLinkClick() {
      setDeepLink('classting://AiTab');
    },

    handleQuizResultModalOpen() {
      this.isQuizResultModal = true;
    },

    handleQuizResultModalClose() {
      this.isQuizResultModal = false;
    },

    getVaildQuizResults() {
      const currectDate = this.$moment().toDate();
      return filter(getLocalStorageItems(sharedPageResultsKey), item => (
        this.$moment(currectDate).isBefore(this.$moment(item.expired_at))
      ));
    },
  },

  beforeMount() {
    this.$wait.start('fetching quiz page');
    const code = this.routeQuery.code;

    if (!code) {
      this.$router.push('/404');
      return;
    }

    const params = {
      code,
    };

    const vaildQuizResults = this.getVaildQuizResults();

    setLocalStorageItems(sharedPageResultsKey, vaildQuizResults);

    this.fetchSharedPage(params)
      .then(() => {
        const vaildQuizResult = find(vaildQuizResults, { code });
        if (!isEmpty(vaildQuizResult)) {
          params.id = vaildQuizResult.shared_page_results_id;
          return this.fetchSharedPageResult(params);
        }

        return true;
      })
      .then(() => setTimeout(() => {
        this.$wait.end('fetching quiz page');
        this.now = now();
      }, 1500))
      .catch(() => this.$router.push('/404'));
  },
};
</script>

<style lang="scss" scoped>
  .shared-page {
    padding-bottom: 84px;
    user-select: none;
    background: #ffffff;
    min-height: 100%;
    overflow: auto;
  }
</style>
